import { PROJECT } from "./project";

export function Docs() {
  return <article className="docs-page">
    <header className="docs-header">
      <h1>Documentation</h1>
      <p>ZKResistor provides immutable, fixed-denomination privacy pools on TON. A withdrawal proves ownership of an unspent deposit without revealing which commitment is being spent.</p>
    </header>

    <nav className="docs-nav" aria-label="Documentation sections">
      <a href="#quick-start">Quick start</a>
      <a href="#create-pool">Create a Pool</a>
      <a href="#secret-note">Secret Note</a>
      <a href="#privacy">Privacy</a>
      <a href="#costs">Costs</a>
      <a href="#broadcast">Broadcast</a>
      <a href="#trust">Trust</a>
    </nav>

    <section className="docs-section" id="quick-start">
      <h2>Quick start</h2>
      <ol className="docs-steps">
        <li><strong>Select a Pool.</strong><span>Each Pool accepts one asset at one fixed amount.</span></li>
        <li><strong>Connect a TON mainnet wallet.</strong><span>The wallet broadcasts deposits and optional self-broadcast withdrawals.</span></li>
        <li><strong>Generate and save the Secret Note.</strong><span>Confirm that it is saved before submitting the deposit.</span></li>
        <li><strong>Withdraw locally.</strong><span>Paste the note, optionally enter a recipient, then generate the proof in your browser.</span></li>
        <li><strong>Choose how to broadcast.</strong><span>Submit through the relayer or directly with the connected wallet.</span></li>
      </ol>
    </section>

    <section className="docs-section" id="create-pool">
      <h2>Create a Pool</h2>
      <p>Anyone can create a fixed-denomination Pool for GRAM or a compatible Jetton. Choose the asset and amount, then submit the Factory message with a connected wallet. The Pool appears in the app only after on-chain activation.</p>
    </section>

    <section className="docs-section" id="secret-note">
      <h2>Secret Note</h2>
      <p>The Secret Note controls withdrawal capability. ZKResistor keeps it in browser memory while the flow is open; it is not uploaded to the API or stored by the protocol.</p>
      <div className="docs-warning"><strong>No recovery.</strong><span>Losing the note permanently loses the ability to withdraw. Anyone who obtains it before a proof is generated can attempt to spend it.</span></div>
    </section>

    <section className="docs-section" id="privacy">
      <h2>Pool metrics</h2>
      <dl className="docs-metrics">
        <div><dt>Shielded TVL</dt><dd>The fixed denomination multiplied by deposits that have not yet been withdrawn.</dd></div>
        <div><dt>Active deposits</dt><dd>The current number of funded positions: total deposits minus total withdrawals.</dd></div>
        <div><dt>Privacy set</dt><dd>Deposits made since the Pool was last empty. Withdrawals do not reduce it until every active deposit has been withdrawn.</dd></div>
        <div><dt>Pool capacity</dt><dd>Lifetime deposit slots used out of 1,048,576 for a depth-20 Pool.</dd></div>
      </dl>
      <p className="docs-muted">The protocol hides the deposit-withdrawal link. Pool choice, denomination, transaction timing, commitments, nullifier hashes and recipient addresses remain public on TON.</p>
    </section>

    <section className="docs-section" id="costs">
      <h2>Costs</h2>
      <dl className="docs-metrics">
        <div><dt>Create a Pool</dt><dd>The app attaches 0.55 GRAM.</dd></div>
        <div><dt>Deposit</dt><dd>GRAM: denomination + 0.37 GRAM. Jetton: denomination + 0.65 GRAM transfer headroom.</dd></div>
        <div><dt>Wallet withdrawal</dt><dd>0.10 GRAM for a GRAM Pool or 0.25 GRAM for a Jetton Pool.</dd></div>
      </dl>
      <p className="docs-muted">ZKResistor charges no protocol fee. Each deposit reserves 0.30 GRAM for its future broadcaster. Unused Jetton transfer headroom is returned through the Jetton wallet excess flow.</p>
    </section>

    <section className="docs-section" id="broadcast">
      <h2>Relayer or wallet</h2>
      <p>The recipient is cryptographically bound to the withdrawal proof. A relayer can broadcast the proof and receive the broadcaster reward, but it cannot replace the recipient or redirect the asset payout.</p>
      <div className="docs-columns">
        <div><h3>Use relayer</h3><p>No withdrawal transaction is sent from the recipient wallet. The public relayer submits the already-generated proof.</p></div>
        <div><h3>Use wallet</h3><p>The connected wallet broadcasts the same proof directly. The recipient may still be a different workchain-0 address.</p></div>
      </div>
    </section>

    <section className="docs-section" id="trust">
      <h2>Trust and verification</h2>
      <p>Contracts have no administrator, upgrade path, allowlist, oracle or off-chain authority. Off-chain indexers improve availability, but cannot authorize deposits or withdrawals.</p>
      <p>The Groth16 keys come from a completed public multi-party ceremony. Security requires at least one participant in each phase to have destroyed their secret randomness.</p>
      <p className="docs-muted">Pools are permissionless. Creating a Jetton Pool does not endorse its token, and a malicious Jetton can affect its own Pool.</p>
      <p className="docs-muted">For Jetton deposits, the hosted app uses a block-anchored, signed indexer response to resolve the user's Jetton wallet. The self-hosted CLI performs this lookup independently through a liteserver.</p>
      <div className="docs-contract">
        <span>Mainnet Factory</span>
        <code>{PROJECT.factoryAddress}</code>
      </div>
      <div className="docs-links">
        <a href={PROJECT.links.contracts} target="_blank" rel="noreferrer noopener">Contracts and protocol</a>
        <a href={PROJECT.links.verifier} target="_blank" rel="noreferrer noopener">TON Verifier</a>
        <a href={PROJECT.links.ceremony} target="_blank" rel="noreferrer noopener">Trusted setup ceremony</a>
        <a href={PROJECT.links.manifest} target="_blank" rel="noreferrer noopener">Protocol manifest</a>
        <a href={PROJECT.links.sdk} target="_blank" rel="noreferrer noopener">TypeScript SDK</a>
        <a href={PROJECT.links.cli} target="_blank" rel="noreferrer noopener">Self-hosted CLI</a>
      </div>
    </section>
  </article>;
}
